import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import { Box } from '@sternum/styled-system';
import { Button, Dialog, DialogProps, TextInput } from '@sternum/ui';
import { namespaces } from '@sternum/web/services/i18n';

import { useCreateDashboard } from '../../hooks';

import { createDashboardValidation } from './CreateDashboardModal.validation';
import { DefaultDashboardName } from './CreateDashboarModal.data';

import { Description, DialogContent, DialogStyled, Title } from './CreateDashboardModal.styles';

export interface CreateDashboardModalProps extends Omit<DialogProps, 'title' | 'theme'> {
  onCancel?: () => unknown;
  onSuccess?: (dashboardId: string) => unknown;
}

type FormValues = {
  name: string;
};

const defaultFormValues: FormValues = {
  name: DefaultDashboardName,
};

export function CreateDashboardModal({ isVisible, ...props }: CreateDashboardModalProps) {
  if (!isVisible) {
    return null;
  }

  return <CreateDashboardModalComponent isVisible={isVisible} {...props} />;
}

function CreateDashboardModalComponent({
  onCancel,
  onSuccess,
  isVisible,
  onCloseDialog,
  ...rest
}: CreateDashboardModalProps) {
  const { t } = useTranslation(namespaces.dashboard);
  const { mutate: createDashboard } = useCreateDashboard();

  const { register, handleSubmit, formState, setError, setFocus } = useForm<FormValues>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(createDashboardValidation()),
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    createDashboard(
      {
        title: values.name,
        isDefault: false,
      },
      {
        onError(error) {
          const message = error.message;

          setError('name', { message });
        },
        onSuccess(response) {
          onSuccess?.(response.id);
        },
      },
    );
  };

  useEffect(() => {
    // wait for ref to be populated
    setTimeout(() => {
      setFocus('name');
    }, 100);
  }, [isVisible, setFocus]);

  return (
    <DialogStyled
      isVisible={isVisible}
      {...rest}
      onCloseDialog={onCloseDialog}
      role="dialog"
      aria-label="create dashboard modal"
      aria-hidden={!isVisible}
      closeOnClickOutside={false}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Title>{t('createDashboardTitle')}</Title>
          <Description>{t('createDashboardDescription')}</Description>

          <Box mt={16}>
            <TextInput
              {...register('name')}
              errorText={formState.errors.name?.message}
              disabled={formState.isSubmitting}
              label={`${t('dashboardNameInputLabel')}*`}
              width="100%"
            />
          </Box>
        </DialogContent>

        <Dialog.Footer isTopBorderVisible={false} alignItems="right">
          <Button
            variant="tertiary"
            aria-label="cancel"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              onCancel?.();
              onCloseDialog?.(e);
            }}
            disabled={formState.isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" aria-label="create" disabled={formState.isSubmitting}>
            {t('create')}
          </Button>
        </Dialog.Footer>
      </form>
    </DialogStyled>
  );
}
